<template>
  <template v-if="access.read || true">
    <SearchInput
      v-model="controller.searchText.value"
      v-if="!isSubTable && settings?.searchpanel"
      :searchAway="true"
    />

    <div
      :class="{
        'table-frame': frame || (verified && !valid),
        'border-danger': verified && !valid,
        'transparent-row': settings?.transparentTr,
      }"
    >
      <div class="navtable">
        <PanelFun
          :items="panelFun"
          v-if="settings?.panelFun"
          classPanelFun="btn-group btn-group-sm mr-1"
          :icon-only="iconOnly"
        />
        <div v-else></div>

        <div class="form-inline" v-if="!isSubTable && pages">
          <Pagination
            @setpage="selectPage"
            v-model="controller.page.value"
            :pages="paginationPages"
            v-if="paginationPages > 1"
          />

          <select
            class="form-control form-control-sm"
            v-model="controller.limit.value"
          >
            <option
              v-for="element of settings?.limits"
              :key="element"
              :value="element"
            >
              {{ element }}
            </option>
          </select>
        </div>
      </div>

      <div class="table-outer" :style="tableOuterStyle">
        <table
          class="table table-striped table-bordered table-hover table-sm unselectable"
          @keydown="onKeyDown"
          tabindex="-1"
        >
          <GridHead
            :controller="controller"
            :checkedRowSelect="checkedRowSelect"
            @sort="onSort"
            :stickyHeader="settings?.stickyHeader"
          />
          <GridBody
            :controller="controller"
            :crossot="settings?.crossot"
            @active="activeRow"
            @select="selectRow"
          />
          <GridFooter :controller="controller" v-if="settings?.footer" />
        </table>
      </div>
    </div>

    <div class="table-invalid-feedback" v-if="feedback && verified">
      {{ feedback }}
    </div>
  </template>
</template>

<script>
//Вывод метки и звездочки если есть валидатор
import { onMounted, computed, defineComponent, onUnmounted, ref } from "vue";

import SearchInput from "@/components/SearchInput.vue";
import Pagination from "@/components/Pagination";
import PanelFun from "@/components/PanelFun";
import GridHead from "./GridHead";
import GridBody from "./GridBody";
import GridFooter from "./GridFooter";
import DBGridController from "./controller";

export default defineComponent({
  inheritAttrs: false,

  emits: ["active", "select", "store", "getEttn"],

  components: {
    SearchInput,
    Pagination,
    PanelFun,
    GridHead,
    GridBody,
    GridFooter,
  },

  props: {
    store: {
      type: Object,
      default: null,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      default: null,
    },
    field: {
      type: String,
      default: null,
    },
    validation: {
      type: Boolean,
      default: true,
    },
    pages: {
      type: Boolean,
      default: true,
    },
    frame: {
      type: Boolean,
      default: false,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const controller = new DBGridController(props, emit);

    if (!controller.store) {
      console.error("Grid storage not installed");

      return {
        access: { read: false },
      };
    }

    emit("store", controller.store);

    const validation = () =>
      controller.validation ? controller.validation() : () => false;

    const isSubTable = computed(() => {
      if (controller.settings?.allowSubtablePagination) {
        return false;
      }
      return !!(
        controller.store.model.subtable || controller.store.model.offLine
      );
    });

    const paginationPages = computed(() => {
      // Локальный расчет количества страниц для вложенных таблиц с allowSubtablePagination
      if (
        controller.settings?.allowSubtablePagination &&
        controller.store?.model?.subtable
      ) {
        const rows = controller.store?.data?.rows || [];
        const limit = controller.limit?.value || rows.length || 1;
        if (!rows.length) return 1;
        return Math.max(1, Math.ceil(rows.length / limit));
      }

      return controller.pages.value || 1;
    });

    onMounted(async () => {
      // Load column settings from backend or localStorage
      if (controller.loadColumnSettings) {
        const settings = await controller.loadColumnSettings();
        if (settings && controller.applyColumnSettings) {
          controller.applyColumnSettings(settings);
        }
      } else if (
        controller.settings?.localStorageKey &&
        localStorage[controller.settings.localStorageKey]
      ) {
        // Legacy support for old localStorage approach
        const fields = JSON.parse(
          localStorage[controller.settings.localStorageKey],
        );

        if (fields.length > 0 && controller.setFields)
          controller.setFields(fields);
      }

      if (!isSubTable.value) {
        controller.store.fetchOptions["filters"] =
          controller.settings?.filters || [];
        controller.store.fetchOptions["data"] = controller.settings?.data || {};

        if (controller.settings?.id)
          controller.store.fetchOptions.id = controller.settings.id;
        if (controller.settings?.owner)
          controller.store.fetchOptions.owner = controller.settings.owner;

        //Установка лимита запускает получение данных
        controller.store.fetchOptions.limit = controller.settings?.limit || 25;

        // await controller.store.fetchData();
      }

      if (props.form && props.field) {
        if (
          controller.stateField?.validation &&
          Object.keys(controller.stateField.validation).length
        )
          props.form.addValidation(validation);
      }

      if (controller.settings?.onCreate)
        controller.settings.onCreate(controller);

      if (controller.settings?.autoOpen && controller.fetchData)
        controller.fetchData();
    });

    onUnmounted(() => {
      if (props.form && props.field) {
        if (
          controller.stateField?.validation &&
          Object.keys(controller.stateField.validation).length
        )
          props.form.delValidation(validation);
      }
    });

    const tableOuterStyle = {};

    // Горизонтальный скролл всегда внутри грида
    tableOuterStyle["overflow-x"] = "auto";

    // Вертикальный скролл включаем только там, где явно задана высота
    if (controller.settings?.height) {
      tableOuterStyle["height"] = controller.settings.height;
      tableOuterStyle["overflow-y"] = "auto";
    }

    return {
      controller,
      openRow: (data) => (controller.openRow ? controller.openRow(data) : null),
      addRow: (data) => (controller.addRow ? controller.addRow(data) : null),
      settings: controller.settings || {},
      tableOuterStyle,
      onKeyDown: (event) =>
        controller.onKeyDown ? controller.onKeyDown(event) : null,
      onSort: (sort) => (controller.onSort ? controller.onSort(sort) : null),
      selectRow: (data) =>
        controller.selectRow ? controller.selectRow(data) : null,
      activeRow: (position) =>
        controller.activeRow ? controller.activeRow(position) : null,
      selectPage: (page) =>
        controller.selectPage ? controller.selectPage(page) : null,
      panelFun: controller.panelFun || ref([]),
      access: computed(
        () =>
          controller.access || {
            read: false,
            create: false,
            update: false,
            delete: false,
          },
      ),
      feedback: controller.feedback || ref(""),
      verified: controller.verified || ref(false),
      valid: controller.valid || ref(true),
      checkedRowSelect: controller.checkedRowSelect || ref(false),
      isSubTable,
      paginationPages,
    };
  },
});
</script>

<style>
.table-outer {
  overflow-x: auto;
  width: 100%;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

/* скрытие скролла если он не нужен */
.table-responsive {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table th,
.table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}
/*  */

.table thead th,
.table thead td {
  outline: 0;
  position: relative;
  -webkit-touch-callout: none;
  /* iOS Safari */
  -webkit-user-select: none;
  /* Chrome/Safari/Opera */
  -khtml-user-select: none;
  /* Konqueror */
  -moz-user-select: none;
  /* Firefox */
  -ms-user-select: none;
  /* Internet Explorer/Edge */
  user-select: none;
}

.table thead .sorting_asc,
.table thead .sorting_desc,
.table thead .sorting {
  cursor: pointer;
}

.table thead .sorting,
.table thead .sorting_asc,
.table thead .sorting_desc,
.table thead .sorting_asc_disabled,
.table thead .sorting_desc_disabled {
  padding-right: 1.5rem;
}

.table thead .sorting:before,
.table thead .sorting:after,
.table thead .sorting_asc:after,
.table thead .sorting_desc:after,
.table thead .sorting_asc_disabled:after,
.table thead .sorting_desc_disabled:after {
  content: "";
  font-family: "emed-icons";
  position: absolute;
  top: 50%;
  right: 0.5rem;
  font-size: 0.75rem;
  margin-top: -0.375rem;
  display: inline-block;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.table thead .sorting:before {
  content: "\e907";
  margin-top: -0.125rem;
  opacity: 0.5;
}

.table thead .sorting:after {
  content: "\e90b";
  margin-top: -0.625rem;
  opacity: 0.5;
}

.table thead .sorting_asc:after {
  content: "\e90b";
  opacity: 1;
}

.table thead .sorting_desc:after {
  content: "\e907";
  opacity: 1;
}

.table thead .sorting_asc_disabled:after {
  content: "";
  opacity: 0.25;
}

.table thead .sorting_desc_disabled:after {
  content: "";
  opacity: 0.25;
}

.table-invalid-feedback {
  width: 100%;
  font-size: 90%;
  color: #f44336;
}

.table-frame {
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.1875rem;
  margin-bottom: 0.625rem;
}

/* .table {
  table-layout: fixed !important;
  width: 100% !important;
  border-collapse: collapse;
}

.table th,
.table td {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  vertical-align: middle;
  min-width: 40px;
}

.table th span {
  display: inline-block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table th {
  position: relative;
  padding-right: 18px;
}

.table th:nth-child(4),
.table td:nth-child(4) {
  min-width: 115px;
} */

.service-th {
  min-width: 20px !important;
  max-width: 40px !important;
  width: 45px !important;
  overflow: visible !important;
  text-overflow: clip !important;
  text-align: center;
}
</style>
